import type { FC } from "react";
import { useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import noop from "lodash/noop";
import styled from "styled-components";

import { Button, DotsLoader, EmptyState, Tab, TabList, TabPanel, Tabs } from "@similarweb/ui-components";
import { Box } from "@similarweb/ui-components/foundations";

import { TableWrap } from "app/pages/keyword-research/keyword-analysis/pages/keyword-generator/google-keyword-generator/styledComponents";
import { keywordGroupsTransforms } from "app/services/keyword-groups";
import { i18nFilter, translate } from "app/filters/i18nFilter";
import { numberFilter } from "app/filters/ngFilters";
import { allTrackers } from "app/services/track/track";

import { ETabs } from "./constants";
import { YoutubeKeywordGeneratorTabTable } from "./YoutubeKeywordGeneratorTabTable";
import { TOGGLE_ON_COMPARE_DROPDOWN } from "app/action_types/website_analysis_action-types";
import { useDispatch } from "react-redux";

const ButtonStyled = styled(Button)`
    margin-top: 16px;
`;

const YOUTUBE_KEYWORD_GROUP_EMPTY_STATE_KEY = "youtube.keyword.generator.keyword.group.empty.state.title";
const TABS = {
    [ETabs.PhraseMatch]: {
        index: ETabs.PhraseMatch,
        totalRecordsFiled: "totalPhraseRecords",
        trackingEventName: "PHRASE-MATCH/youtube",
    },
    [ETabs.RelatedKeywords]: {
        index: ETabs.RelatedKeywords,
        totalRecordsFiled: "totalRelatedRecords",
        trackingEventName: "RELATED KEYWORDS/youtube",
    },
};

interface IYoutubeKeywordGeneratorTablesProps {
    onTabSelect: (tab: ETabs) => void;
    selectedTab: ETabs;
}

const KeywordGroupEmptyStateButton = () => {
    const dispatch = useDispatch();
    return (
        <ButtonStyled
            type="outlined"
            onClick={() => dispatch({ type: TOGGLE_ON_COMPARE_DROPDOWN })}
        >
            {translate(YOUTUBE_KEYWORD_GROUP_EMPTY_STATE_KEY)}
        </ButtonStyled>
    );
};

const KeywordGroupEmptyState = ({ titleKey }: { titleKey: string }) => (
    <Box width="100%">
        <EmptyState
            title={translate(titleKey)}
            CtaButtons={KeywordGroupEmptyStateButton}
        />
    </Box>
);

export const YoutubeKeywordGeneratorTables: FC<IYoutubeKeywordGeneratorTablesProps> = (props) => {
    const { onTabSelect = noop, selectedTab = ETabs.PhraseMatch } = props;
    const { params } = useCurrentStateAndParams();
    const isKeywordGroup = keywordGroupsTransforms.isKeywordGroup(params.keyword);
    const [titleData, setTitleData] = useState({
        totalPhraseRecords: isKeywordGroup ? 0 : -1,
        totalRelatedRecords: isKeywordGroup ? 0 : -1,
    });

    const handleTabSelect = (tab: ETabs) => {
        allTrackers.dispatchTrackEvent("Tab", "click", TABS[tab].trackingEventName);
        onTabSelect(tab);
    };
    const createDataCallback = (tab) => (totalRecords) => {
        setTitleData({ ...titleData, [tab]: totalRecords });
    };
    const onDataError = (tab, err) => {
        setTitleData({ ...titleData, [tab]: null });
    };
    const getDataCallbackPhrase = (totalRecords) => {
        createDataCallback(TABS[ETabs.PhraseMatch].totalRecordsFiled)(totalRecords);
    };
    const getDataCallbackRelated = (totalRecords) => {
        createDataCallback(TABS[ETabs.RelatedKeywords].totalRecordsFiled)(totalRecords);
    };
    const onDataErrorPhrase = (err) => onDataError(TABS[ETabs.PhraseMatch].totalRecordsFiled, err);
    const onDataErrorRelated = (err) => onDataError(TABS[ETabs.RelatedKeywords].totalRecordsFiled, err);

    const getTabTitle = (id, stateParam) => {
        return (
            <div style={{ display: "flex", alignItems: "center" }}>
                {i18nFilter()(`youtube.keyword.generator.tab.${id}.title`, {
                    total: titleData[stateParam] > 0 ? `(${numberFilter()(titleData[stateParam])})` : "",
                })}
                {titleData[stateParam] === -1 ? (
                    <div style={{ marginLeft: "55px" }}>
                        <DotsLoader />
                    </div>
                ) : null}
            </div>
        );
    };

    return (
        <TableWrap>
            <Tabs
                onSelect={handleTabSelect}
                forceRenderTabPanel={true}
                defaultIndex={selectedTab}
            >
                <TabList>
                    <Tab tooltipText={i18nFilter()("youtube.keyword.generator.page.tab.phrase.tooltip")}>
                        {getTabTitle("phrase", TABS[ETabs.PhraseMatch].totalRecordsFiled)}
                    </Tab>
                    <Tab tooltipText={i18nFilter()("youtube.keyword.generator.page.tab.related.tooltip")}>
                        {getTabTitle("related", TABS[ETabs.RelatedKeywords].totalRecordsFiled)}
                    </Tab>
                </TabList>
                <TabPanel>
                    {isKeywordGroup ? (
                        <KeywordGroupEmptyState titleKey={YOUTUBE_KEYWORD_GROUP_EMPTY_STATE_KEY} />
                    ) : (
                        <YoutubeKeywordGeneratorTabTable
                            selectedTab={ETabs.PhraseMatch}
                            preventCountTracking={selectedTab !== ETabs.PhraseMatch}
                            totalRecords={titleData[TABS[ETabs.PhraseMatch].totalRecordsFiled]}
                            getDataCallback={getDataCallbackPhrase}
                            onDataError={onDataErrorPhrase}
                        />
                    )}
                </TabPanel>
                <TabPanel>
                    {isKeywordGroup ? (
                        <KeywordGroupEmptyState titleKey={YOUTUBE_KEYWORD_GROUP_EMPTY_STATE_KEY} />
                    ) : (
                        <YoutubeKeywordGeneratorTabTable
                            selectedTab={ETabs.RelatedKeywords}
                            preventCountTracking={selectedTab !== ETabs.RelatedKeywords}
                            totalRecords={titleData[TABS[ETabs.RelatedKeywords].totalRecordsFiled]}
                            getDataCallback={getDataCallbackRelated}
                            onDataError={onDataErrorRelated}
                        />
                    )}
                </TabPanel>
            </Tabs>
        </TableWrap>
    );
};
